import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import moment from 'moment';
import { getConditionalStatusBadgeClass, getStatusDisplayText } from '../../common/Utils/color';
import UtilizationBars from '../UtilizationBars/UtilizationBars';
import Pagination from '../Pagination/Pagination';
import { useTaskExecutionHistory } from '../services/activityService';

export interface PatchDeviceRow {
  agentId: string;
  hostName: string;
  status: string;
  startTime: string;
  endTime: string;
  cpuUsage: string;
  memoryUsage: string;
  // Raw task message/stackTrace - shown in the row's expand accordion, same as DeviceInsightTable.
  activity?: string;
  stackTrace?: string;
  // Same per-task-type detail shapes DeviceInsightTable uses to phrase the Activity column.
  packageDetail?: {
    currentVersion: string | null;
    targetVersion: string | null;
    name: string | null;
    actionType: string | null;
  } | null;
  scriptExecution?: { name: string | null } | null;
  registryUpdate?: { name: string | null; actionType: string | null } | null;
  windowsUpdate?: { kbId: string | null; title: string | null; category: string | null; installType: string | null } | null;
  restartAgent?: { activity: string | null } | null;
  shutdown?: { activity: string | null } | null;
  endpointSync?: { activity: string | null } | null;
  shipAgentLogsTask?: { activity: string | null } | null;
}

interface TaskHistoryItem {
  id: string;
  activity: string;
  startTime: string;
  endTime: string;
  status: string;
  cpuUsage: string;
  memoryUsage: string;
  message: string;
}

interface PatchInsightPanelProps {
  // taskId of the patch/activity itself - paired with each row's agentId to look up that
  // device's execution history for this specific task.
  taskId: string;
  patchLabel: string;
  patchType: string;
  // Raw backend RemediationPlanActionType (e.g. "SoftwareUpdate") - every row shares this since
  // they're all executions of the same patch/activity, unlike DeviceInsightTable where each row
  // can be a different task type.
  rawTaskType: string;
  currentVersion?: string;
  targetVersion?: string;
  rows: PatchDeviceRow[];
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

const formatDateTime = (value?: string | null): string => {
  if (!value || value.startsWith('0001-01-01')) return 'NA';
  return moment(value).format('DD MMM YYYY, hh:mm A');
};

const showValue = (val?: string | null): string => (val && val !== 'NA' && val.trim() !== '' ? val : '');

// Mirrors DeviceInsightTable's compareVersions - upgrade/downgrade/update label for software updates.
const compareVersions = (current: string | null, target: string | null): 'upgrade' | 'downgrade' | 'update' => {
  if (!current || !target) return 'update';
  try {
    const currParts = current.split('.').map((p) => parseInt(p, 10));
    const targetParts = target.split('.').map((p) => parseInt(p, 10));
    const maxLen = Math.max(currParts.length, targetParts.length);

    for (let i = 0; i < maxLen; i++) {
      const curr = currParts[i] || 0;
      const tgt = targetParts[i] || 0;
      if (tgt > curr) return 'upgrade';
      if (tgt < curr) return 'downgrade';
    }
    return 'update';
  } catch {
    return 'update';
  }
};

// Mirrors DeviceInsightTable's renderActivityContent, keyed off the shared patch task type
// instead of a per-row type since every row here is the same activity applied to a different device.
const renderActivityContent = (row: PatchDeviceRow, rawTaskType: string): JSX.Element => {
  const taskType = rawTaskType?.toLowerCase() || 'unknown';
  const { actionType, name, currentVersion, targetVersion } = row.packageDetail || {
    actionType: null,
    name: null,
    currentVersion: null,
    targetVersion: null,
  };

  switch (taskType) {
    case 'softwareinstall': {
      const _name = showValue(name);
      const _target = showValue(targetVersion);
      return (
        <span>
          <span className="font-semibold">Install</span> {_name}
          {_target && <span className="text-green-400"> v{_target}</span>}
        </span>
      );
    }

    case 'softwareupdate': {
      const _name = showValue(name);
      const _current = showValue(currentVersion);
      const _target = showValue(targetVersion);
      const updateDirection = compareVersions(currentVersion, targetVersion);
      const directionLabel = updateDirection === 'upgrade' ? 'Upgrade' : updateDirection === 'downgrade' ? 'Downgrade' : 'Update';

      return (
        <span>
          <span className="font-semibold">{directionLabel}</span> {_name}
          {(_current || _target) && (
            <span className="text-gray-300">
              {' '}
              <span className="text-red-400">{_current}</span>
              <span className="mx-1 text-gray-400">→</span>
              <span className="text-green-400">{_target}</span>
            </span>
          )}
        </span>
      );
    }

    case 'softwareuninstall': {
      const _name = showValue(name);
      return <span>Uninstall {_name}</span>;
    }

    case 'script': {
      const scriptName = showValue(row.scriptExecution?.name);
      return scriptName ? <span>Script: {scriptName}</span> : <span>Script Execution</span>;
    }

    case 'registryupdate': {
      const registryName = showValue(row.registryUpdate?.name);
      const registryAction = showValue(row.registryUpdate?.actionType);
      return (
        <span>
          Registry {registryAction ? registryAction.toUpperCase() : 'Update'}
          {registryName && <span className="text-gray-300"> - {registryName}</span>}
        </span>
      );
    }

    case 'rebootdevice': {
      const restartActivity = showValue(row.restartAgent?.activity);
      return <span>{restartActivity || 'Device Reboot'}</span>;
    }

    case 'windowsosupdate': {
      const updateTitle = showValue(row.windowsUpdate?.title);
      const kbId = showValue(row.windowsUpdate?.kbId);
      const installType = showValue(row.windowsUpdate?.installType);
      return (
        <span>
          {installType && <span>{installType} - </span>}
          Windows Update
          {kbId && <span> [{kbId}]</span>}
          {updateTitle && <span> - {updateTitle}</span>}
        </span>
      );
    }

    case 'shutdowndevice': {
      const shutdownActivity = showValue(row.shutdown?.activity);
      return <span>{shutdownActivity || 'System Shutdown'}</span>;
    }

    case 'restartagent': {
      const agentActivity = showValue(row.restartAgent?.activity);
      return <span>{agentActivity || 'Agent Restart'}</span>;
    }

    case 'endpointsync': {
      const syncActivity = showValue(row.endpointSync?.activity);
      return <span>{syncActivity || 'Endpoint Sync'}</span>;
    }

    case 'shipagentlogs': {
      const logsActivity = showValue(row.shipAgentLogsTask?.activity);
      return <span>{logsActivity || 'Ship Agent Logs'}</span>;
    }

    case 'installpatchifiagent':
      return <span>Install Patchifi Agent</span>;

    case 'getnearbydiscoverabledevices':
      return <span>Discover Nearby Devices</span>;

    default: {
      const activityMessage = showValue(row.activity);
      return <span>{activityMessage || taskType || 'Unknown Task'}</span>;
    }
  }
};

// The inverse of DeviceInsightTable: given one patch, show every device it ran on (backend-paginated).
const PatchInsightPanel: React.FC<PatchInsightPanelProps> = ({
  taskId,
  patchLabel,
  patchType,
  rawTaskType,
  currentVersion,
  targetVersion,
  rows,
  loading = false,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
}) => {
  const { getTaskExecutionHistory } = useTaskExecutionHistory();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<TaskHistoryItem[]>([]);
  const [historyHostName, setHistoryHostName] = useState('');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const toggleExpand = (idx: number) => {
    setExpandedRow((prev) => (prev === idx ? null : idx));
  };

  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryData([]);
    setHistoryError(null);
  };

  const handleOpenHistory = async (agentId: string, hostName: string) => {
    setHistoryHostName(hostName);
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);

    if (!agentId || !taskId) {
      setHistoryError('Missing identifiers to load execution history.');
      setHistoryData([]);
      setHistoryLoading(false);
      return;
    }

    try {
      const response = await getTaskExecutionHistory(agentId, taskId);

      const entries: TaskHistoryItem[] = (response?.data || []).map((history: any, idx: number) => {
        const startTime =
          history.taskExecutionStartedAt || history.taskExecutionStartedAtUtc || history.taskExecutionStartTime || history.startDateTime;
        const endTime = history.timestamp || history.taskExecutionEndTime || history.taskExecutionEndedAt || history.endDateTime;

        return {
          id: history.id || `${idx}`,
          activity: showValue(history.taskType) || showValue(history.message) || 'NA',
          startTime: formatDateTime(startTime),
          endTime: formatDateTime(endTime),
          status: showValue(history.status) || 'NA',
          cpuUsage: showValue(history.resourceUsageMetrics?.avgCPUUsage) || 'NA',
          memoryUsage: showValue(history.resourceUsageMetrics?.avgRAMUsage) || 'NA',
          message: history.message || '',
        };
      });

      setHistoryData(entries);
    } catch (error) {
      console.error('Failed to load task execution history:', error);
      setHistoryError('Unable to load execution history right now.');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="p-5 text-white">
      <div
        className="flex flex-wrap items-center gap-3 mb-4 p-4 rounded-xl border"
        style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}
      >
        <span
          className="px-3 py-1 rounded-full text-white text-xs font-medium border"
          style={{ backgroundColor: '#252525', borderColor: '#333' }}
        >
          {patchType}
        </span>
        <span className="text-sm text-gray-200 font-medium">{patchLabel}</span>
        {(currentVersion || targetVersion) && (
          <span className="text-xs whitespace-nowrap">
            {currentVersion && <span className="text-red-400">{currentVersion}</span>}
            {currentVersion && targetVersion && <span className="mx-1 text-gray-400">→</span>}
            {targetVersion && <span className="text-green-400">{targetVersion}</span>}
          </span>
        )}
        <span className="text-xs text-gray-500 ml-auto">
          {rows.length} device{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      <table className="w-full text-sm table-auto border-collapse">
        <thead className="bg-zinc-900">
          <tr className="text-left">
            <th className="p-2">Host Name</th>
            <th className="p-2">Activity</th>
            <th className="p-2">Start Time</th>
            <th className="p-2">End Time</th>
            <th className="p-2">Utilization</th>
            <th className="p-2">Status</th>
            <th className="p-2 text-center">Audit</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} className="text-center py-6 text-gray-400">
                Loading devices...
              </td>
            </tr>
          ) : rows.length > 0 ? (
            rows.map((row, idx) => (
              <React.Fragment key={`${row.agentId}-${idx}`}>
                <tr
                  className={`cursor-pointer hover:bg-zinc-700 ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'}`}
                  onClick={() => toggleExpand(idx)}
                >
                  <td className="py-3 px-3 text-red-400">{row.hostName}</td>
                  <td className="py-3 px-3">{renderActivityContent(row, rawTaskType)}</td>
                  <td className="py-3 px-3">{row.startTime}</td>
                  <td className="py-3 px-3">{row.endTime}</td>
                  <td className="py-3 px-3">
                    <UtilizationBars cpu={row.cpuUsage} ram={row.memoryUsage} cpuLabel="CPU Usage" ramLabel="Memory Usage" />
                  </td>
                  <td className="py-3 px-3">
                    <span className={getConditionalStatusBadgeClass(row.status)}>{getStatusDisplayText(row.status)}</span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-900 hover:bg-zinc-700 border border-zinc-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenHistory(row.agentId, row.hostName);
                      }}
                      title={taskId ? 'View audit history' : 'No task available'}
                      disabled={!taskId}
                    >
                      <FaSearch />
                    </button>
                  </td>
                </tr>

                {/* Message / Stack Trace accordion, same as DeviceInsightTable's row expand */}
                <tr>
                  <td colSpan={7} className="p-0">
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedRow === idx ? 'max-h-40 opacity-100 p-4' : 'max-h-0 opacity-0 p-0'
                      } bg-zinc-700 text-gray-300`}
                    >
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <strong>Message:</strong> {row.activity || 'NA'}
                        </div>
                        {row.stackTrace && (
                          <div>
                            <strong>Stack Trace:</strong>
                            {row.stackTrace}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan={7}>
                <div className="flex items-center justify-center py-12 px-4">
                  <div className="text-center">
                    <p className="text-gray-400 font-medium mb-1">No devices require this patch</p>
                    <p className="text-gray-500 text-sm">All devices are already up to date with this {patchType.toLowerCase()}</p>
                  </div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {onPageChange && <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={onPageChange} />}

      {/* Audit Modal - rendered via portal to break out of slide-over stacking context */}
      {historyModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] overflow-hidden">
              <div className="flex items-start justify-between p-4 border-b border-zinc-700">
                <div>
                  <p className="text-lg font-semibold">Audit</p>
                  <p className="text-xs text-gray-400 mt-1">{historyHostName}</p>
                </div>
                <button
                  type="button"
                  className="text-gray-300 hover:text-white text-xl leading-none"
                  onClick={closeHistoryModal}
                  aria-label="Close Audit modal"
                >
                  X
                </button>
              </div>

              <div className="p-4 overflow-auto max-h-[70vh] space-y-3">
                {historyError && <div className="bg-red-900 border border-red-700 text-red-100 p-3 rounded">{historyError}</div>}

                {historyLoading ? (
                  <div className="flex items-center justify-center py-6 text-gray-300">
                    <FaSpinner className="animate-spin mr-2" />
                    <span>Loading Audit...</span>
                  </div>
                ) : historyData.length > 0 ? (
                  <table className="w-full text-sm table-auto border-collapse">
                    <thead className="bg-zinc-800">
                      <tr className="text-left">
                        <th className="p-2">Activity</th>
                        <th className="p-2">Start Time</th>
                        <th className="p-2">End Time</th>
                        <th className="p-2">Utilization</th>
                        <th className="p-2">Message</th>
                        <th className="p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((item, idx) => (
                        <tr key={item.id} className={idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800'}>
                          <td className="py-3 px-3">{item.activity}</td>
                          <td className="py-3 px-3">{item.startTime}</td>
                          <td className="py-3 px-3">{item.endTime}</td>
                          <td className="py-3 px-3">
                            <UtilizationBars cpu={item.cpuUsage} ram={item.memoryUsage} cpuLabel="CPU Usage" ramLabel="Memory Usage" />
                          </td>
                          <td className="py-3 px-3">
                            <p className="line-clamp-3 break-words">{item.message || 'NA'}</p>
                          </td>
                          <td className="py-3 px-3">
                            <span className={getConditionalStatusBadgeClass(item.status)}>{getStatusDisplayText(item.status)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center text-gray-400 py-6">No audit list available.</div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default PatchInsightPanel;
